import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { Fragment, memo, useMemo } from "react";

import { isUnbuiltRoute } from "@/configs";
import { useIsLoggedIn, useTranslate } from "@/hooks";
import { useSelector } from "@/store";
import { CheckSidebar } from "@/types";

import NavigationGroup from "./NavigationGroup";

type SidebarGroup = { key: string; parent?: string; items: CheckSidebar[] };

const groupByParent = (items: CheckSidebar[]): SidebarGroup[] => {
  const sorted = [...items].sort((a, b) => a.order - b.order);
  const groups: SidebarGroup[] = [];
  const groupIndexByKey = new Map<string, number>();
  for (const item of sorted) {
    const parent = item.parent?.trim() || "";
    const key = parent || "__ungrouped__";
    let index = groupIndexByKey.get(key);
    if (index === undefined) {
      index = groups.length;
      groupIndexByKey.set(key, index);
      groups.push({ key, parent: parent || undefined, items: [] });
    }
    groups[index].items.push(item);
  }
  return groups;
};

function MenuContent({ toggleDrawer }: { toggleDrawer?: () => void }) {
  const isLoggedIn = useIsLoggedIn();
  const t = useTranslate();
  const sidebars = useSelector((state) => state.app.bootstrap)?.sidebars ?? [];

  const groups = useMemo(() => {
    const visible = sidebars.filter(
      (item) =>
        item.status === "active" &&
        (item.isLoggedIn === true ? isLoggedIn : true) &&
        !isUnbuiltRoute(item.url),
    );
    return groupByParent(visible);
  }, [sidebars, isLoggedIn]);

  return (
    <Stack component="nav" aria-label={t("common.main-navigation")} justifyContent="space-between">
      {groups.map((group, index) => (
        <Fragment key={group.key}>
          {index > 0 && <Divider role="separator" />}
          <NavigationGroup title={group.parent} items={group.items} toggleDrawer={toggleDrawer} />
        </Fragment>
      ))}
    </Stack>
  );
}

export default memo(MenuContent);
